import React, { useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { AlertCircle, RotateCcw, ShieldAlert } from "lucide-react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { getReport, refundBookingByBookingNo } from "../../../api";
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import TablesV2 from "../../../components/common/Tables/TablesV2";
import Background from "../../Login/Background";
import { useNightMode } from "../../../contexts/NightModeContext";
import { useParams } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

const RefundSearch: React.FC = () => {
  const { isNightMode } = useNightMode();

  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<any[]>([]);
  const [error, setError] = useState("");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [refunding, setRefunding] = useState(false);
  const [showRefundConfirmation, setShowRefundConfirmation] = useState(false);
  const { eventId } = useParams<{ eventId: string }>();

  useEffect(() => {
    let mounted = true;
    const loadBookings = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getReport(eventId);
        const arr = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.bookings)
              ? response.bookings
              : [];
        if (mounted) setBookings(arr.filter(isPrimaryBooking));
      } catch (err) {
        console.error("Error loading bookings:", err);
        if (mounted) setError("Failed to load bookings.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadBookings();
    return () => {
      mounted = false;
    };
  }, [eventId]);

  const getContact = (booking: any) => booking.bookingObject?.primaryContactDetails || booking.Contact || {};
  const getName = (booking: any) => {
    const contact = getContact(booking);
    return `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || booking.name || "Guest";
  };
  const isPrimaryBooking = (booking: any) => (
    booking.bookedBy === 0
    || booking.bookedBy === "0"
    || !booking.primaryBookingNo
    || booking.bookingNo === booking.primaryBookingNo
  );
  const getRefundStatus = (booking: any) => {
    const statuses = [booking.paymentStatus, booking.refundStatus, booking.bookingStatus]
      .map((status) => String(status || "").toLowerCase());
    return statuses.some((status) => status.includes("refunded")) ? "Refunded" : "Not Refunded";
  };

  const getRowId = (booking: any, index: number) => String(booking.bookingId || booking.bookingNo || `refund-${index}`);
  const getRefundAmount = (booking: any) => Number(booking.organizerRevenue || 0);
  const selectedBookings = useMemo(
    () => bookings.filter((booking, index) => rowSelection[getRowId(booking, index)]),
    [bookings, rowSelection]
  );
  const totalTickets = selectedBookings.reduce((total, booking) => total + Number(booking.totalTicketCount || 0), 0);
  const totalRefund = selectedBookings.reduce((total, booking) => total + getRefundAmount(booking), 0);

  const confirmRefund = async () => {
    if (selectedBookings.some((booking) => getRefundStatus(booking) === "Refunded")) {
      toast.info("Already refunded bookings cannot be refunded again.");
      setShowRefundConfirmation(false);
      return;
    }
    try {
      setRefunding(true);
      const bookingNumbers = Array.from(new Set(
        selectedBookings
          .map((booking) => booking.primaryBookingNo || booking.bookingNo)
          .filter(Boolean)
      ));
      await Promise.all(bookingNumbers.map((bookingNo) => (
        refundBookingByBookingNo(bookingNo)
      )));
      const selectedIds = new Set(selectedBookings.map((booking, index) => getRowId(booking, index)));
      setBookings((currentBookings) => currentBookings.map((booking, index) => (
        selectedIds.has(getRowId(booking, index))
          ? { ...booking, paymentStatus: "refunded", refundStatus: "refunded" }
          : booking
      )));
      setShowRefundConfirmation(false);
      setRowSelection({});
      toast.success("Refund successful");
    } catch (err) {
      console.error("Unable to issue refund:", err);
      toast.error("Unable to issue refund.");
    } finally {
      setRefunding(false);
    }
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <input type="checkbox" aria-label="Select all bookings on this page" checked={table.getIsAllPageRowsSelected()} ref={(input) => { if (input) input.indeterminate = table.getIsSomePageRowsSelected(); }} onChange={table.getToggleAllPageRowsSelectedHandler()} />
        ),
        cell: ({ row }) => <input type="checkbox" aria-label={`Select booking ${row.original.bookingNo || row.id}`} checked={row.getIsSelected()} disabled={!row.getCanSelect()} onChange={row.getToggleSelectedHandler()} />,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "bookingNo",
        header: "BOOKING NUMBER",
        cell: ({ row }: any) => (
          <span className="font-mono fs-12px fw-bold" style={{ color: isNightMode ? "#ffffff" : "#0f172a" }}>{row.original.bookingNo || "N/A"}</span>
        ),
      },
      {
        id: "details",
        accessorFn: (booking) => {
          const contact = getContact(booking);
          return `${getName(booking)} ${contact.email || booking.email || "N/A"} ${contact.phoneNo || booking.phone || "N/A"}`;
        },
        header: "BOOKING DETAILS",
        cell: ({ row }: any) => {
          const contact = getContact(row.original);
          return (
            <div>
              <div className="contact-name-text fs-13px fw-bold">{getName(row.original)}</div>
              <div className="contact-sub-text fs-12px">{contact.email || row.original.email || "N/A"}</div>
              <div className="font-mono contact-sub-text fs-12px">{contact.phoneNo || row.original.phone || "N/A"}</div>
            </div>
          );
        },
      },
      {
        id: "paymentStatus",
        accessorFn: (booking) => getRefundStatus(booking) === "Refunded" ? "refunded" : booking.paymentStatus || "pending",
        header: "PAYMENT STATUS",
        cell: ({ row }: any) => {
          const status = getRefundStatus(row.original) === "Refunded"
            ? "refunded"
            : String(row.original.paymentStatus || "pending").toLowerCase();
          const isPaid = status === "paid";
          const isRefunded = status === "refunded";
          return (
            <span
              style={{
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "11px",
                fontWeight: 700,
                background: isRefunded ? "rgba(238, 63, 124, 0.15)" : isPaid ? "rgba(16, 185, 129, 0.15)" : "rgba(148, 163, 184, 0.15)",
                color: isRefunded ? "#EE3F7C" : isPaid ? "#34D399" : "#94A3B8",
                border: isRefunded ? "1px solid rgba(238, 63, 124, 0.35)" : isPaid ? "1px solid rgba(16, 185, 129, 0.35)" : "1px solid rgba(148, 163, 184, 0.3)",
                whiteSpace: "nowrap",
              }}
            >
              {isPaid ? "Paid" : isRefunded ? "Refunded" : status.toUpperCase()}
            </span>
          );
        },
      },
    ],
    [isNightMode]
  );

  return (
    <div className={`v3-wrap px-3 py-4 ${isNightMode ? "" : "light-theme"}`} style={{ position: "relative" }}>
      <Background />
      <ToastContainer position="top-right" autoClose={3000} theme={isNightMode ? "dark" : "light"} />

      <PageHeader
        title="Refund Search"
        isLoading={loading}
      />

      <div className="d-flex align-items-center justify-content-end gap-3 mb-4">
        {Object.keys(rowSelection).length > 0 && <span className="contact-sub-text fs-12px">{Object.keys(rowSelection).length} selected</span>}
        <button
            type="button"
          disabled={selectedBookings.length === 0}
          onClick={() => setShowRefundConfirmation(true)}
            style={{
              padding: "10px 24px",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "linear-gradient(135deg, #EE3F7C 0%, #F79B59 100%)",
              color: "#ffffff",
              border: "none",
              cursor: "pointer",
              opacity: selectedBookings.length === 0 ? 0.5 : 1,
              boxShadow: "0 4px 14px rgba(238, 63, 124, 0.35)",
              whiteSpace: "nowrap",
            }}
          >
            <RotateCcw size={15} /> Refund
          </button>
      </div>

      {error && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: 12,
            background: "rgba(255,93,115,0.1)",
            border: "1px solid rgba(255,93,115,0.25)",
            color: "#FF5D73",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 16,
          }}
        >
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {showRefundConfirmation && (
        <Modal
          isOpen
          toggle={() => setShowRefundConfirmation(false)}
          centered
          size="lg"
          contentClassName="subuser-modal-content"
        >
          <ModalHeader
            toggle={() => setShowRefundConfirmation(false)}
            style={{
              padding: "20px 22px 16px",
              borderBottom: `1px solid ${isNightMode ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
            }}
          >
            <div className="d-flex align-items-center gap-3">
              <span
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "linear-gradient(135deg, #EE3F7C 0%, #F79B59 100%)",
                  color: "#ffffff",
                  boxShadow: "0 4px 14px rgba(238, 63, 124, 0.4)",
                  flexShrink: 0,
                }}
              >
                <ShieldAlert size={20} />
              </span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: isNightMode ? "#94a3b8" : "#64748b", marginBottom: 2 }}>
                  Review Action
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: isNightMode ? "#ffffff" : "#0f172a" }}>Refund Confirmation</div>
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-4">
            <div
              className="mb-4"
              style={{
                position: "relative",
                overflow: "hidden",
                padding: "14px 16px 14px 20px",
                borderRadius: 14,
                background: isNightMode ? "rgba(238, 63, 124, 0.10)" : "rgba(238, 63, 124, 0.06)",
                border: `1px solid ${isNightMode ? "rgba(238, 63, 124, 0.35)" : "rgba(238, 63, 124, 0.25)"}`,
                lineHeight: 1.6,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  background: "linear-gradient(180deg, #EE3F7C 0%, #F79B59 100%)",
                }}
              />
              <span style={{ fontSize: 13, color: isNightMode ? "#ffffff" : "#0f172a" }}>
                You are about to refund <strong>{selectedBookings.length}</strong> booking(s) with a total refund amount of <strong style={{ color: "#EE3F7C" }}>${totalRefund.toFixed(2)}</strong>. This action cannot be undone.
              </span>
            </div>

            <div className="mb-4" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
              {[
                { label: "Selected Bookings", value: `${selectedBookings.length}` },
                { label: "Total Tickets", value: `${totalTickets}` },
                { label: "Total Refund", value: `$${totalRefund.toFixed(2)}`, emphasized: true },
              ].map((stat) => (
                <div
                  key={stat.label}
                  style={{
                    padding: "12px 14px",
                    borderRadius: 12,
                    background: isNightMode ? "rgba(255,255,255,0.045)" : "rgba(15,23,42,0.035)",
                    border: `1px solid ${isNightMode ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.08)"}`,
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: isNightMode ? "#94a3b8" : "#64748b", marginBottom: 6 }}>
                    {stat.label}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "JetBrains Mono, monospace", color: stat.emphasized ? "#EE3F7C" : isNightMode ? "#e2e8f0" : "#0f172a" }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: isNightMode ? "#94a3b8" : "#64748b", marginBottom: 8 }}>
              Preview
            </div>
            <div
              style={{
                borderRadius: 12,
                border: `1px solid ${isNightMode ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.08)"}`,
                background: isNightMode ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)",
                overflow: "hidden",
              }}
            >
              <div style={{ maxHeight: 240, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: isNightMode ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" }}>
                      <th style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: isNightMode ? "#94a3b8" : "#64748b", textAlign: "left", whiteSpace: "nowrap" }}>Booking No</th>
                      <th style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: isNightMode ? "#94a3b8" : "#64748b", textAlign: "left", whiteSpace: "nowrap" }}>Name</th>
                      <th style={{ padding: "10px 14px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: isNightMode ? "#94a3b8" : "#64748b", textAlign: "right", whiteSpace: "nowrap" }}>Refund Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBookings.map((booking) => (
                      <tr key={booking.bookingId || booking.bookingNo} style={{ borderTop: `1px solid ${isNightMode ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)"}` }}>
                        <td style={{ padding: "10px 14px", fontFamily: "JetBrains Mono, monospace", fontSize: 12, color: isNightMode ? "#ffffff" : "#0f172a" }}>{booking.bookingNo || "N/A"}</td>
                        <td style={{ padding: "10px 14px", fontSize: 13, color: isNightMode ? "#e2e8f0" : "#334155" }}>{getName(booking)}</td>
                        <td style={{ padding: "10px 14px", fontFamily: "JetBrains Mono, monospace", fontSize: 13, fontWeight: 700, color: "#EE3F7C", textAlign: "right" }}>${getRefundAmount(booking).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </ModalBody>
          <ModalFooter
            style={{
              padding: "16px 22px 20px",
              borderTop: `1px solid ${isNightMode ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
            }}
          >
            <Button
              type="button"
              onClick={() => setShowRefundConfirmation(false)}
              style={{
                padding: "9px 22px",
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 600,
                border: `1px solid ${isNightMode ? "rgba(255,255,255,0.25)" : "rgba(15,23,42,0.15)"}`,
                background: isNightMode ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.9)",
                color: isNightMode ? "#e2e8f0" : "#475569",
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmRefund}
              disabled={refunding}
              style={{
                padding: "9px 24px",
                borderRadius: 10,
                fontSize: 13,
                fontWeight: 700,
                border: "none",
                background: "linear-gradient(135deg, #EE3F7C 0%, #F79B59 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 14px rgba(238, 63, 124, 0.35)",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <RotateCcw size={14} /> {refunding ? "Processing..." : "Confirm Refund"}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      <div style={{ position: "relative", zIndex: 10 }}>
        <TablesV2
          data={bookings}
          columns={columns}
          isLoading={loading}
          enableColumnFilters
          columnFilterPlaceholder=""
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          canSelectRow={(row) => getRefundStatus(row) !== "Refunded"}
          getRowId={getRowId}
          loadingMessage="Loading bookings..."
          emptyMessage="No bookings found."
        />
      </div>
    </div>
  );
};

export default RefundSearch;